/* Native, section-scoped carousel; no library or image animation required. */
if (!customElements.get('arb-studio-carousel')) {
  customElements.define('arb-studio-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const on = (node, type, handler, options = {}) => node.addEventListener(type, handler, { ...options, signal: this.controller.signal });
      this.slides = [...this.querySelectorAll('.ash__slide')];
      this.index = 0;
      this.mobile = matchMedia('(max-width:760px)');
      this.elapsed = 0;
      this.duration = (Number(this.dataset.interval) || 6) * 1000;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.dataset.autoplay !== 'true' || this.motion.matches;
      this.inView = true;
      this.focused = false;
      this.editorSelected = false;
      this.slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === 0);
        slide.inert = i !== 0;
        slide.setAttribute('aria-hidden', String(i !== 0));
      });
      this.style.setProperty('--ash-directory-y', '32px');
      this.querySelectorAll('[data-index]').forEach(button => {
        const active = Number(button.dataset.index) === 0;
        button.tabIndex = active ? 0 : -1;
        if (active) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
      this.syncViewport();
      on(this.mobile, 'change', () => this.syncViewport());
      this.classList.add('ash--ready');
      if (this.slides.length < 2) return;
      this.querySelector('.ash__controls').hidden = this.visibleIndices.length < 2;
      this.querySelectorAll('[data-index]').forEach(button => on(button, 'click', () => this.go(Number(button.dataset.index), true)));
      on(this.querySelector('.ash__arrow--prev'), 'click', () => this.go(this.nextIndex(-1), true));
      on(this.querySelector('.ash__arrow--next'), 'click', () => this.go(this.nextIndex(1), true));
      on(this.querySelector('.ash__pause'), 'click', () => { this.paused = !this.paused; this.updatePause(); });
      on(this, 'keydown', event => {
        if (/INPUT|TEXTAREA|SELECT/.test(event.target.tagName)) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault(); this.go(this.nextIndex(event.key === 'ArrowRight' ? 1 : -1), true);
        }
      });
      on(this, 'pointermove', event => {
        if (event.pointerType !== 'mouse') return;
        this.classList.add('ash--pointer-active');
        clearTimeout(this.idleTimer);
        this.idleTimer = setTimeout(() => this.classList.remove('ash--pointer-active'), 1800);
      }, { passive: true });
      on(this, 'pointerleave', () => this.classList.remove('ash--pointer-active'));
      on(this, 'focusin', () => { this.focused = true; });
      on(this, 'focusout', event => { this.focused = this.contains(event.relatedTarget); });
      on(this, 'pointerdown', event => {
        if (event.pointerType !== 'touch' || event.target.closest('a,button')) return;
        this.touch = { x: event.clientX, y: event.clientY };
      }, { passive: true });
      on(this, 'pointerup', event => {
        if (!this.touch) return;
        const dx = event.clientX - this.touch.x, dy = event.clientY - this.touch.y;
        this.touch = null;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.4) this.go(this.nextIndex(dx < 0 ? 1 : -1), true);
      }, { passive: true });
      on(this, 'pointercancel', () => { this.touch = null; });
      on(document, 'visibilitychange', () => { this.lastFrame = performance.now(); });
      on(this.motion, 'change', () => { if (this.motion.matches) this.paused = true; this.updatePause(); });
      on(document, 'shopify:block:select', event => {
        const index = this.slides.findIndex(slide => slide.dataset.blockId === event.detail?.blockId);
        if (index < 0) return;
        this.editorSelected = true; this.go(index);
      });
      on(document, 'shopify:block:deselect', event => {
        if (this.slides.some(slide => slide.dataset.blockId === event.detail?.blockId)) this.editorSelected = false;
      });
      this.observer = new IntersectionObserver(entries => { this.inView = entries[0].isIntersecting; }, { threshold: 0.1 });
      this.observer.observe(this);
      this.updatePause();
      this.loadSlide(this.nextIndex(1));
      this.lastFrame = performance.now();
      const tick = now => {
        const delta = Math.min(now - this.lastFrame, 100);
        this.lastFrame = now;
        if (this.visibleIndices.length > 1 && !this.paused && !this.focused && !this.editorSelected && this.inView && !document.hidden) {
          this.elapsed += delta;
          this.style.setProperty('--ash-progress', Math.min(this.elapsed / this.duration, 1));
          if (this.elapsed >= this.duration) this.go(this.nextIndex(1));
        }
        this.frame = requestAnimationFrame(tick);
      };
      this.frame = requestAnimationFrame(tick);
    }
    nextIndex(direction) {
      const position = this.visibleIndices.indexOf(this.index);
      return this.visibleIndices[(position + direction + this.visibleIndices.length) % this.visibleIndices.length];
    }
    syncViewport() {
      this.visibleIndices = this.slides.map((slide, i) => i).filter(i => !this.mobile.matches || this.slides[i].dataset.desktopOnly !== 'true');
      this.querySelectorAll('[data-index]').forEach(button => { button.hidden = !this.visibleIndices.includes(Number(button.dataset.index)); });
      this.visibleIndices.forEach((i, p) => this.slides[i].setAttribute('aria-label', (p + 1) + ' / ' + this.visibleIndices.length));
      const controls = this.querySelector('.ash__controls');
      if (controls) controls.hidden = this.visibleIndices.length < 2;
      if (!this.visibleIndices.includes(this.index)) this.go(this.visibleIndices[0]);
      this.updateTone();
    }
    updateTone() {
      this.classList.toggle('ash--dark-controls', this.slides[this.index].dataset.controlsTone === 'dark');
    }
    loadSlide(index) {
      const slide = this.slides[(index + this.slides.length) % this.slides.length];
      slide.querySelectorAll('[data-srcset]').forEach(image => { image.srcset = image.dataset.srcset; delete image.dataset.srcset; });
      slide.querySelectorAll('[data-src]').forEach(image => { image.src = image.dataset.src; delete image.dataset.src; });
    }
    go(index, announce = false) {
      const next = (index + this.slides.length) % this.slides.length;
      if (!this.visibleIndices.includes(next)) return;
      this.loadSlide(next);
      this.elapsed = 0;
      this.style.setProperty('--ash-progress', 0);
      if (next === this.index) return;
      const previous = this.slides[this.index];
      const focusInSlide = previous.contains(document.activeElement);
      const focusInNav = this.contains(document.activeElement) && document.activeElement.matches('[data-index]');
      this.index = next;
      this.updateTone();
      this.slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === next);
        slide.inert = i !== next;
        slide.setAttribute('aria-hidden', String(i !== next));
      });
      this.querySelectorAll('[data-index]').forEach(button => {
        button.tabIndex = Number(button.dataset.index) === next ? 0 : -1;
        if (Number(button.dataset.index) === next) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
      this.style.setProperty('--ash-directory-y', `${(1 - next) * 32}px`);
      if (focusInSlide || focusInNav) {
        const selector = matchMedia('(max-width:760px)').matches ? '.ash__segment' : '.ash__directory-item';
        this.querySelector(`${selector}[data-index="${next}"]`).focus({ preventScroll: true });
      }
      if (announce) this.querySelector('.ash__sr-only').textContent = `${this.visibleIndices.indexOf(next) + 1} / ${this.visibleIndices.length}: ${this.slides[next].dataset.label}`;
      this.loadSlide(this.nextIndex(1));
    }
    updatePause() {
      this.classList.toggle('ash--paused', this.paused);
      this.querySelector('.ash__pause')?.setAttribute('aria-label', this.paused ? 'Play slideshow' : 'Pause slideshow');
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      clearTimeout(this.idleTimer);
      cancelAnimationFrame(this.frame);
      this.observer?.disconnect();
    }
  });
}
